import React, {
  ChangeEvent,
  useCallback,
  useRef,
  useReducer,
  useEffect,
  useMemo,
  useId,
} from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from 'styled-components';
import Fit from 'react-fit';

import { Theme } from '../../../themeBase';
import { KEYBOARD_KEYS } from '../../../constants';
import { useKeypress, useOnClickOutside } from '../../../hooks';
import Options from '../Options';

import { SelectWrapper, StyledSelect, FocusHandler } from './Select.styles';
import { DropdownIcon } from './components';
import { selectReducer, selectActions, initialState } from './reducer';
import { getStatus } from './utils';
import { iconSize } from './constants';

import { SelectOptionType, SelectProps } from './types';

const Select = ({
  options,
  onSelect,
  filterable,
  placeholder,
  variant = 'primary',
  selectSize = 'medium',
  borderVariant = 'primary',
  disabled,
  hasError,
  selectedValue,
  searchLabel = 'Type to search',
  selectOptionLabel = 'Select value',
  name,
  id,
  onBlur,
  portalContainer,
  dropdownVariant = 'primary',
  dropdownAlignment = 'right',
  targetElementRef,
}: SelectProps) => {
  const [
    {
      optionsVisible,
      filteredOptions,
      searchPhrase,
      placeholderContent,
      selectionIndex,
      inSelectionMode,
    },
    dispatch,
  ] = useReducer(selectReducer, {
    ...initialState,
    placeholderContent: placeholder || selectOptionLabel,
  });
  const selectAutoCompleteId = `select-${useId()}`;
  const theme = useTheme() as Theme;
  const dropDownIconColor = useMemo(() => {
    const {
      components: {
        Select: {
          componentVariants: { dropdownVariant: component },
        },
      },
    } = theme;
    return component.variants[dropdownVariant].color as string;
  }, [theme, dropdownVariant]);

  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const portalElement = portalContainer?.current;

  const matchedIndex = selectedValue
    ? options.findIndex(({ value }) => value === selectedValue)
    : -1;

  const selectedItem: SelectOptionType =
    matchedIndex > -1
      ? options[matchedIndex]
      : {
          label: selectedValue.toString() || '',
          value: selectedValue || '',
        };

  const onFilterOptions = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    const filtered = options.filter((option) =>
      option.label.toString().toLowerCase().startsWith(value.toLowerCase())
    );

    dispatch(selectActions.setSelectionIndex(null));
    dispatch(selectActions.setFilteredOptions(filtered));
    dispatch(selectActions.setSearchPhrase(value));
  };

  const onInputFocus = () => {
    if (filterable) {
      dispatch(selectActions.setPlaceholder(searchLabel));
    }
    if (!optionsVisible) {
      dispatch(selectActions.toggleSelectionMode(true));
      dispatch(selectActions.showOptions());
    }
  };

  const onInputBlur = (item: SelectOptionType) => {
    dispatch(selectActions.setSearchPhrase(''));
    dispatch(selectActions.hideOptions());
    dispatch(selectActions.toggleSelectionMode(false));
    dispatch(selectActions.setSelectionIndex(null));
    if (onBlur) onBlur(item);
  };

  const onValueSelect = (item: SelectOptionType) => {
    dispatch(selectActions.hideOptions());
    onSelect(item);
    onInputBlur(item);
  };

  useOnClickOutside(
    [containerRef, portalContainer],
    () => {
      if (optionsVisible) {
        dispatch(selectActions.hideOptions());
      }
    },
    targetElementRef?.current
  );

  const selectOptions = searchPhrase ? filteredOptions : options;

  const keyboardHandler = useCallback(
    (e: KeyboardEvent) => {
      switch (e.key) {
        case KEYBOARD_KEYS.ENTER:
          if (typeof selectionIndex === 'number') {
            const item = selectOptions[selectionIndex];
            dispatch(selectActions.setSelectionIndex(null));
            onValueSelect(item);
          }
          break;
        case KEYBOARD_KEYS.UP:
          e.preventDefault();
          if (selectionIndex > 0) {
            dispatch(selectActions.setSelectionIndex(selectionIndex - 1));
          }
          break;
        case KEYBOARD_KEYS.DOWN:
          e.preventDefault();
          if (selectionIndex === null) {
            dispatch(selectActions.setSelectionIndex(0));
          } else if (selectionIndex < selectOptions.length - 1) {
            dispatch(selectActions.setSelectionIndex(selectionIndex + 1));
          }
          break;
        case KEYBOARD_KEYS.ESCAPE:
        case KEYBOARD_KEYS.TAB:
          onInputBlur(selectedItem);
          break;
      }
    },
    [selectionIndex, selectedItem, selectOptions]
  );

  useKeypress({
    keyboardAction: keyboardHandler,
    handledKeys: [
      KEYBOARD_KEYS.ENTER,
      KEYBOARD_KEYS.ESCAPE,
      KEYBOARD_KEYS.UP,
      KEYBOARD_KEYS.DOWN,
      KEYBOARD_KEYS.TAB,
    ],
    addEventListenerCondition: optionsVisible,
    eventListenerDependencies: [optionsVisible],
  });

  useEffect(() => {
    if (optionsVisible && matchedIndex !== null && matchedIndex > -1) {
      dispatch(selectActions.setSelectionIndex(matchedIndex));
    }
  }, [optionsVisible]);

  useEffect(() => {
    const scrollHandler = () => {
      if (optionsVisible) onInputBlur(selectedItem);
    };

    if (optionsVisible && containerRef.current?.offsetParent) {
      containerRef.current.offsetParent.addEventListener(
        'scroll',
        scrollHandler
      );
    }

    return () => {
      if (containerRef.current?.offsetParent)
        containerRef.current.offsetParent.removeEventListener(
          'scroll',
          scrollHandler
        );
    };
  }, [optionsVisible, selectedItem]);

  const getSelectValue = () => {
    if (!inSelectionMode || !filterable) return selectedItem.label;
    return searchPhrase;
  };

  const renderOptions = () => (
    <Options
      alignment={dropdownAlignment}
      options={selectOptions}
      selectionIndex={selectionIndex}
      onSelect={(e, item) => {
        e.stopPropagation();
        onValueSelect(item);
      }}
      selectVariant={variant}
      selectSize={selectSize}
      borderVariant={borderVariant}
    />
  );

  const calculateDropdownPosition = useCallback(() => {
    const { height, width, left, top } =
      containerRef.current.getBoundingClientRect();

    return {
      left: left + window.scrollX,
      top: top + height + window.scrollY,
      width,
    };
  }, [portalContainer]);

  return (
    <SelectWrapper ref={containerRef}>
      <StyledSelect
        type="text"
        ref={inputRef}
        autoComplete={selectAutoCompleteId}
        name={name}
        id={id}
        readOnly={!filterable}
        value={getSelectValue()}
        onChange={onFilterOptions}
        onClick={onInputFocus}
        status={getStatus(disabled, hasError)}
        placeholder={placeholderContent}
        selectSize={selectSize}
        selectVariant={variant}
        borderVariant={borderVariant}
        data-testid="select"
        tabIndex={-1}
      />
      <FocusHandler tabIndex={0} onFocus={onInputFocus} />
      <DropdownIcon
        isExpanded={optionsVisible}
        onClick={() => {
          if (!optionsVisible) onInputFocus();
        }}
        size={iconSize}
        iconColor={dropDownIconColor}
      />
      {optionsVisible && selectOptions.length > 0 ? (
        <>
          {portalElement ? (
            createPortal(
              <div
                style={{ position: 'absolute', ...calculateDropdownPosition() }}
              >
                {renderOptions()}
              </div>,
              portalElement
            )
          ) : (
            <Fit>{renderOptions()}</Fit>
          )}
        </>
      ) : null}
    </SelectWrapper>
  );
};

export default Select;
